'use client';
import { ToneCountWithUser } from '@/utils/helpers';
import { LinkButton } from '../Button';
import { ToneCardRow } from '../ToneSection/ToneCardRow';
import { ToneRowTracking } from '../ToneSection/ToneRowTracking';
import { useState } from 'react';
import { GEAR } from '@/utils/constants';
import { useScrollFade, scrollFadeMask } from '@/hooks/useScrollFade';
import { GearChip } from '@/components/ui/GearChip/GearChip';
import { Music } from 'lucide-react';

export interface TrendingLane {
  gear: GEAR;
  tones: ToneCountWithUser[];
}

interface Props {
  title?: string;
  // Prefetched lanes for every gear category. Empty lanes are kept so their
  // chip still shows; selecting one renders an empty state.
  lanes: TrendingLane[];
}

export const TrendingTonesWithFilters = ({ title, lanes }: Props) => {
  const [activeGear, setActiveGear] = useState<GEAR | undefined>(lanes[0]?.gear);

  // Edge-fade the chip row toward whichever side still has off-screen chips
  // (mobile overflow). When everything fits, no fade is applied.
  const fade = useScrollFade([lanes]);

  if (lanes.length === 0) return null;

  const fadeStyle = scrollFadeMask(fade.atStart, fade.atEnd);

  const activeLane = lanes.find((l) => l.gear === activeGear) ?? lanes[0];
  const tones = activeLane.tones;

  return (
    <div className="max-w-6xl px-4 mx-auto sm:px-6 flex flex-col gap-8">
      <div className="flex flex-col gap-6">
        <h2 className="text-2xl font-bold text-white truncate">{title || 'Trending Tones'}</h2>
        {/* Horizontally scrollable gear-lane chips (replaces the old tab bar). */}
        <div
          ref={fade.ref}
          onScroll={fade.onScroll}
          style={fadeStyle}
          className="flex flex-row items-center gap-3 overflow-x-auto [&::-webkit-scrollbar]:hidden [scrollbar-width:none] -mx-4 px-4 sm:-mx-6 sm:px-6"
        >
          {lanes.map(({ gear }) => (
            <GearChip
              key={gear}
              gear={gear}
              isActive={gear === activeLane.gear}
              onClick={() => setActiveGear(gear)}
            />
          ))}
        </div>
      </div>
      {/* Reserve a tone-card row's height so an empty lane doesn't collapse
          or shift layout when switching between gear chips. */}
      {tones.length === 0 ? (
        <div className="flex items-center justify-center min-h-[500px]">
          <div className="flex flex-col max-w-[320px] items-center justify-center px-8 py-8 text-center bg-zinc-900 rounded-xl">
            <div className="mb-4">
              <Music className="flex-shrink-0 text-zinc-400" size={40} />
            </div>
            <h2 className="text-lg font-semibold text-white mb-2">No trending tones</h2>
            <p className="text-zinc-400">Check back soon.</p>
          </div>
        </div>
      ) : (
        <ToneRowTracking surface="home_trending_filtered" tones={tones}>
          <div className="flex flex-col gap-8 relative">
            <ToneCardRow tones={tones} />
          </div>
        </ToneRowTracking>
      )}
      <div className="flex w-full justify-center items-center pt-4">
        <LinkButton href={`/search`} className="text-sm sm:text-base w-full md:w-[230px]" isPrimary>
          View All
        </LinkButton>
      </div>
    </div>
  );
};
